import { type FC } from "react";
import { useTranslation } from "@privacy/translations";
import { useForm } from "react-hook-form";
import { updateInformationTypeMutationOptions, type UpdateInformationTypePayload } from "../../api/informationTypes";
import * as FormDialog from "../../shared/FormDialog";
import { useMutation } from "@tanstack/react-query";

type InformationTypeEditDialogProps = { onClose: () => void; informationTypeId: string; informationTypeName: string };

export const InformationTypeEditDialog: FC<InformationTypeEditDialogProps> = ({
  onClose,
  informationTypeId,
  informationTypeName,
}) => {
  const { t } = useTranslation();

  const { handleSubmit, control, setFocus } = useForm<UpdateInformationTypePayload>({
    values: { name: informationTypeName },
  });
  const updateMutation = useMutation(updateInformationTypeMutationOptions());

  FormDialog.useAutoFocusField(setFocus, "name");

  return (
    <FormDialog.Dialog isOpen={true}>
      <FormDialog.Title>{t("InformationType.Edit.DialogHeader")}</FormDialog.Title>

      <FormDialog.Form
        onSubmit={handleSubmit(payload =>
          updateMutation.mutate({ id: informationTypeId, payload }, { onSuccess: () => onClose() })
        )}
      >
        <FormDialog.Content>
          <FormDialog.FormError isError={updateMutation.isError} />
          <FormDialog.Fields>
            <FormDialog.TextField
              control={control}
              name="name"
              rules={{ required: t("InformationType.Create.Dialog.Error") }}
              label={t("InformationType.Create.Dialog.NameLabel")}
              placeholder={t("InformationType.Create.Dialog.NamePlaceholder")}
            />
          </FormDialog.Fields>
        </FormDialog.Content>

        <FormDialog.ActionButtons>
          <FormDialog.CancelButton onClick={onClose} />
          <FormDialog.SubmitButton isSubmitting={updateMutation.isPending}>
            {t("InformationType.Edit.SubmitButton")}
          </FormDialog.SubmitButton>
        </FormDialog.ActionButtons>
      </FormDialog.Form>
    </FormDialog.Dialog>
  );
};
